import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { checkRequired, apiFieldErrors, firstErrorMessage, FieldError, Req, ERROR_COLOR } from '../utils/formErrors';

const TX_LABELS = { category: 'Catégorie', amount: 'Montant', date: 'Date', department: 'Département', description: 'Description', reference: 'Référence' };
const NAME_LABELS = { name: 'Nom', monthly_limit: 'Limite mensuelle', monthly_budget: 'Budget mensuel', type: 'Type' };
const errBorder = (msg) => (msg ? { borderColor: ERROR_COLOR } : {});

// ─── Icons ────────────────────────────────────────────────────────────────────
const SvgIcon = ({ d, size = 16, strokeWidth = 2, fill = 'none' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {d}
  </svg>
);
const IC = (d, fill) => ({ size, sw } = {}) => <SvgIcon d={d} size={size || 16} strokeWidth={sw || 2} fill={fill || 'none'} />;

const IncomeIcon  = IC(<><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></>);
const ExpenseIcon = IC(<><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></>);
const BalanceIcon = IC(<><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></>);
const AlertIcon   = IC(<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>);
const PlusIcon    = IC(<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>);
const EditIcon    = IC(<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>);
const TrashIcon   = IC(<><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></>);
const TagIcon     = IC(<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></>);
const ListIcon    = IC(<><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></>);
const ChartIcon   = IC(<><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></>);
const CloseIcon   = IC(<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>);
const CheckIcon   = IC(<polyline points="20 6 9 17 4 12"/>);
const InfoIcon    = IC(<><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>);
const FilterIcon  = IC(<><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></>);
const CalIcon     = IC(<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>);
const TrendUpIcon  = IC(<><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></>);
const DownloadIcon = IC(<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>);
const ExcelIcon    = IC(<><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 8l5 8M12 8l-5 8"/><path d="M17 8v8M15 8h4M15 12h4M15 16h4"/></>);
const PdfIcon      = IC(<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/><line x1="9" y1="11" x2="15" y2="11"/></>);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt   = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const MONTHS_SHORT = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
const pColor = (p) => p >= 100 ? '#ef4444' : p >= 80 ? '#f59e0b' : '#10b981';
const nowY = new Date().getFullYear();
const nowM = new Date().getMonth() + 1;

// ─── Shared micro-styles ──────────────────────────────────────────────────────
const inp = {
  width: '100%', boxSizing: 'border-box', padding: '8px 11px',
  borderRadius: '8px', border: '1px solid var(--border)',
  background: 'var(--card-bg)', color: 'var(--text-primary)',
  fontSize: '0.84rem', outline: 'none', fontFamily: 'inherit',
};
const lbl = { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px', letterSpacing: '0.04em' };
const btnP = {
  display:'inline-flex', alignItems:'center', gap:'6px', padding:'9px 16px',
  borderRadius:'8px', border:'none', cursor:'pointer', fontSize:'0.84rem', fontWeight:600,
  background:'var(--primary)', color:'#ffffff', transition:'opacity 0.15s', fontFamily:'inherit',
};
const btnS = {
  display:'inline-flex', alignItems:'center', gap:'6px', padding:'8px 14px',
  borderRadius:'8px', cursor:'pointer', fontSize:'0.84rem', fontWeight:500,
  background:'var(--card-bg)', color:'var(--text-primary)',
  border:'1px solid var(--border)', transition:'all 0.15s', fontFamily:'inherit',
};
const card = {
  background:'var(--card-bg)', border:'1px solid var(--border)',
  borderRadius:'12px', padding:'20px',
};

// ─── Bar Chart amélioré ───────────────────────────────────────────────────────
function BarChart({ data }) {
  const [hover, setHover] = useState(null);
  if (!data?.length) return <div style={{ textAlign:'center', padding:'30px', color:'var(--text-secondary)', fontSize:'0.82rem' }}>Pas de données</div>;
  const max = Math.max(...data.map(d => Math.max(d.income, d.expense)), 1);
  return (
    <div style={{ position:'relative' }}>
      {hover !== null && data[hover] && (
        <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', background:'var(--text-primary)', color:'var(--card-bg)', borderRadius:8, padding:'6px 12px', fontSize:'0.75rem', fontWeight:600, zIndex:10, whiteSpace:'nowrap', pointerEvents:'none' }}>
          {MONTHS_SHORT[data[hover].month-1]} — Entrées : {fmt(data[hover].income)} · Sorties : {fmt(data[hover].expense)}
        </div>
      )}
      <div style={{ display:'flex', alignItems:'flex-end', gap:'8px', height:'120px', marginTop:'28px' }}>
        {data.map((d, i) => (
          <div key={i} onMouseEnter={()=>setHover(i)} onMouseLeave={()=>setHover(null)}
            style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'5px', height:'100%', justifyContent:'flex-end', cursor:'default' }}>
            <div style={{ width:'100%', display:'flex', gap:'3px', alignItems:'flex-end', justifyContent:'center' }}>
              <div style={{ flex:1, background: hover===i ? '#059669' : '#10b981', borderRadius:'4px 4px 0 0',
                height:`${(d.income/max)*108}px`, minHeight:d.income>0?4:0, transition:'all 0.2s' }} />
              <div style={{ flex:1, background: hover===i ? '#dc2626' : '#ef4444', borderRadius:'4px 4px 0 0',
                height:`${(d.expense/max)*108}px`, minHeight:d.expense>0?4:0, transition:'all 0.2s' }} />
            </div>
            <span style={{ fontSize:'0.62rem', color: hover===i ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: hover===i?700:400 }}>
              {MONTHS_SHORT[d.month-1]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Modal Transaction ─────────────────────────────────────────────────────────
function TransactionModal({ transaction, categories, departments = [], onClose, onSave }) {
  const [form, setForm] = useState(transaction || {
    category:'', amount:'', date: new Date().toISOString().slice(0,10), description:'', reference:'', department:''
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const found = checkRequired(form, { category: 'Catégorie', amount: 'Montant', date: 'Date' });
    setErrors(found);
    if (Object.keys(found).length) return;
    setSaving(true);
    const payload = { ...form, category: parseInt(form.category, 10), amount: parseFloat(form.amount), department: form.department ? parseInt(form.department, 10) : null };
    try {
      form.id
        ? await api.put(`/accounting/transactions/${form.id}/`, payload)
        : await api.post('/accounting/transactions/', payload);
      toast.success(form.id ? 'Transaction modifiée' : 'Transaction ajoutée');
      onSave();
    } catch (err) {
      const found = apiFieldErrors(err, TX_LABELS);
      setErrors(found);
      toast.error(firstErrorMessage(found, TX_LABELS, 'Erreur lors de la sauvegarde'));
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={onClose}>
      <div style={{ ...card, width:'100%', maxWidth:'480px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,0.3)', padding:'0', borderRadius:'16px' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding:'18px 22px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700, color:'var(--text-primary)' }}>
              {form.id ? 'Modifier la transaction' : 'Nouvelle transaction'}
            </h3>
            <p style={{ margin:'2px 0 0', fontSize:'0.78rem', color:'var(--text-secondary)' }}>Renseignez les informations de la transaction</p>
          </div>
          <button onClick={onClose} style={{ background:'var(--page-bg)', border:'1px solid var(--border)', borderRadius:'8px', cursor:'pointer', color:'var(--text-secondary)', display:'flex', padding:'6px', alignItems:'center' }}><CloseIcon /></button>
        </div>
        <form onSubmit={submit} noValidate style={{ padding:'22px' }}>
          <div style={{ marginBottom:'16px' }}>
            <label style={lbl}>Catégorie<Req /></label>
            <select name="category" value={form.category} onChange={set} style={{ ...inp, ...errBorder(errors.category) }}>
              <option value="">Sélectionner une catégorie...</option>
              <optgroup label="— Entrées">
                {categories.filter(c=>c.type==='income').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
              <optgroup label="— Sorties">
                {categories.filter(c=>c.type==='expense').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            </select>
            <FieldError message={errors.category} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'16px' }}>
            <div>
              <label style={lbl}>Montant (FCFA)<Req /></label>
              <input name="amount" type="number" min="0" value={form.amount} onChange={set} style={{ ...inp, ...errBorder(errors.amount) }} placeholder="0" />
              <FieldError message={errors.amount} />
            </div>
            <div>
              <label style={lbl}>Date<Req /></label>
              <input name="date" type="date" value={form.date} onChange={set} style={{ ...inp, ...errBorder(errors.date) }} />
              <FieldError message={errors.date} />
            </div>
          </div>
          {departments.length > 0 && (
            <div style={{ marginBottom:'16px' }}>
              <label style={lbl}>Département</label>
              <select name="department" value={form.department || ''} onChange={set} style={inp}>
                <option value="">Aucun département</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          )}
          <div style={{ marginBottom:'16px' }}>
            <label style={lbl}>Description</label>
            <input name="description" value={form.description} onChange={set} style={inp} placeholder="Optionnel" />
          </div>
          <div style={{ marginBottom:'24px' }}>
            <label style={lbl}>Référence</label>
            <input name="reference" value={form.reference} onChange={set} style={inp} placeholder="N° reçu, chèque... (optionnel)" />
          </div>
          <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
            <button type="button" onClick={onClose} style={btnS}>Annuler</button>
            <button type="submit" disabled={saving} style={{ ...btnP, opacity: saving ? 0.7 : 1 }}>{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal Catégorie ───────────────────────────────────────────────────────────
function CategoryModal({ category, onClose, onSave }) {
  const [form, setForm] = useState(category || { name:'', type:'expense', color:'#ef4444', monthly_limit:'' });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const found = checkRequired(form, { name: 'Nom' });
    setErrors(found);
    if (Object.keys(found).length) return;
    setSaving(true);
    const payload = { ...form, monthly_limit: form.monthly_limit || null };
    try {
      form.id
        ? await api.put(`/accounting/categories/${form.id}/`, payload)
        : await api.post('/accounting/categories/', payload);
      toast.success(form.id ? 'Catégorie modifiée' : 'Catégorie créée');
      onSave();
    } catch (err) {
      const found = apiFieldErrors(err, NAME_LABELS);
      setErrors(found);
      toast.error(firstErrorMessage(found, NAME_LABELS, 'Erreur lors de la sauvegarde'));
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }} onClick={onClose}>
      <div style={{ ...card, width:'100%', maxWidth:'440px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,0.3)', padding:'0', borderRadius:'16px' }} onClick={e => e.stopPropagation()}>
        <div style={{ padding:'18px 22px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700, color:'var(--text-primary)' }}>
              {form.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </h3>
            <p style={{ margin:'2px 0 0', fontSize:'0.78rem', color:'var(--text-secondary)' }}>Configurez la catégorie et sa limite</p>
          </div>
          <button onClick={onClose} style={{ background:'var(--page-bg)', border:'1px solid var(--border)', borderRadius:'8px', cursor:'pointer', color:'var(--text-secondary)', display:'flex', padding:'6px' }}><CloseIcon /></button>
        </div>
        <form onSubmit={submit} noValidate style={{ padding:'22px' }}>
          <div style={{ marginBottom:'16px' }}>
            <label style={lbl}>Nom<Req /></label>
            <input name="name" value={form.name} onChange={set} style={{ ...inp, ...errBorder(errors.name) }} placeholder="Ex: Offrandes, Loyer, Électricité..." />
            <FieldError message={errors.name} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'16px' }}>
            <div>
              <label style={lbl}>Type</label>
              <select name="type" value={form.type} onChange={set} style={inp}>
                <option value="income">Entrée (recette)</option>
                <option value="expense">Sortie (dépense)</option>
              </select>
            </div>
            <div>
              <label style={lbl}>Couleur</label>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <input name="color" type="color" value={form.color} onChange={set} style={{ width:38, height:38, borderRadius:8, border:'1px solid var(--border)', cursor:'pointer', padding:2, background:'var(--card-bg)' }} />
                <span style={{ fontSize:'0.78rem', color:'var(--text-secondary)', fontFamily:'monospace' }}>{form.color}</span>
              </div>
            </div>
          </div>
          {form.type === 'expense' && (
            <div style={{ marginBottom:'16px' }}>
              <label style={lbl}>Limite mensuelle (FCFA)</label>
              <input name="monthly_limit" type="number" min="0" value={form.monthly_limit} onChange={set} style={inp} placeholder="Laisser vide = pas de limite" />
              <p style={{ margin:'4px 0 0', fontSize:'0.73rem', color:'var(--text-secondary)' }}>Alerte orange à 80%, rouge à 100%</p>
            </div>
          )}
          <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end', marginTop:'8px' }}>
            <button type="button" onClick={onClose} style={btnS}>Annuler</button>
            <button type="submit" disabled={saving} style={{ ...btnP, opacity: saving ? 0.7 : 1 }}>{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color, Icon, trend }) {
  return (
    <div style={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:'14px', padding:'20px 22px', borderTop:`3px solid ${color}` }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
        <div style={{ fontSize:'0.71rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-secondary)' }}>{label}</div>
        <div style={{ width:34, height:34, borderRadius:'9px', background:`${color}1a`, display:'flex', alignItems:'center', justifyContent:'center', color }}>
          <Icon size={15} />
        </div>
      </div>
      <div style={{ fontSize:'1.8rem', fontWeight:800, color, lineHeight:1, marginBottom:'2px' }}>{value}</div>
      <div style={{ fontSize:'0.72rem', color:'var(--text-secondary)', marginBottom: sub ? '12px' : 0 }}>FCFA</div>
      {sub && (
        <div style={{ paddingTop:'12px', borderTop:'1px solid var(--border)', fontSize:'0.76rem', color:'var(--text-secondary)' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ─── Filtre période (composant partagé) ───────────────────────────────────────
function PeriodFilter({ month, year, dateFrom, dateTo, type, cat, categories, onMonth, onYear, onDateFrom, onDateTo, onType, onCat, onReset, showType = true, showCat = true }) {
  const hasCustom = dateFrom || dateTo;
  const hasFilter = hasCustom || type || cat;
  return (
    <div style={{ background:'var(--page-bg)', border:'1px solid var(--border)', borderRadius:'12px', padding:'14px 16px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'12px' }}>
        <FilterIcon />
        <span style={{ fontSize:'0.8rem', fontWeight:700, color:'var(--text-primary)' }}>Filtres</span>
        {hasFilter && (
          <button onClick={onReset} style={{ marginLeft:'auto', ...btnS, padding:'4px 10px', fontSize:'0.75rem' }}>
            Réinitialiser
          </button>
        )}
      </div>
      <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', alignItems:'flex-end' }}>
        {/* Mois / Année */}
        <div>
          <label style={lbl}>Mois</label>
          <select value={month} onChange={e => { onMonth(Number(e.target.value)); onDateFrom(''); onDateTo(''); }}
            style={{ ...inp, width:'110px' }} disabled={hasCustom}>
            {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Année</label>
          <select value={year} onChange={e => { onYear(Number(e.target.value)); onDateFrom(''); onDateTo(''); }}
            style={{ ...inp, width:'86px' }} disabled={hasCustom}>
            {[2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Séparateur */}
        <div style={{ display:'flex', alignItems:'center', color:'var(--text-secondary)', fontSize:'0.75rem', paddingBottom:'1px' }}>ou</div>

        {/* Plage personnalisée */}
        <div>
          <label style={lbl}>Du</label>
          <input type="date" value={dateFrom} onChange={e => onDateFrom(e.target.value)} style={{ ...inp, width:'140px' }} />
        </div>
        <div>
          <label style={lbl}>Au</label>
          <input type="date" value={dateTo} onChange={e => onDateTo(e.target.value)} style={{ ...inp, width:'140px' }} />
        </div>

        {/* Type */}
        {showType && (
          <div>
            <label style={lbl}>Type</label>
            <select value={type} onChange={e => onType(e.target.value)} style={{ ...inp, width:'120px' }}>
              <option value="">Tous</option>
              <option value="income">Entrées</option>
              <option value="expense">Sorties</option>
            </select>
          </div>
        )}

        {/* Catégorie */}
        {showCat && (
          <div>
            <label style={lbl}>Catégorie</label>
            <select value={cat} onChange={e => onCat(e.target.value)} style={{ ...inp, width:'150px' }}>
              <option value="">Toutes</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Modal Département ── */
function DepartmentModal({ dept, onClose, onSave }) {
  const [form, setForm] = useState(dept || { name:'', description:'', color:'#6366f1', monthly_budget:'' });
  const [saving, setSaving] = useState(false);
  const set = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const [errors, setErrors] = useState({});
  const submit = async e => {
    e.preventDefault();
    const found = checkRequired(form, { name: 'Nom' });
    setErrors(found);
    if (Object.keys(found).length) return;
    setSaving(true);
    try {
      const payload = { ...form, monthly_budget: form.monthly_budget || null };
      if (dept?.id) await api.put(`/accounting/departments/${dept.id}/`, payload);
      else          await api.post('/accounting/departments/', payload);
      toast.success(dept?.id ? 'Département modifié' : 'Département créé');
      onSave();
    } catch (err) {
      const found = apiFieldErrors(err, NAME_LABELS);
      setErrors(found);
      toast.error(firstErrorMessage(found, NAME_LABELS, 'Erreur lors de la sauvegarde'));
    }
    finally  { setSaving(false); }
  };

  const PRESET_COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#3b82f6','#8b5cf6','#ec4899','#14b8a6'];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={onClose}>
      <div style={{ ...card, width:'100%', maxWidth:'420px', boxShadow:'0 32px 80px rgba(0,0,0,0.3)', padding:0, borderRadius:16 }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding:'20px 24px 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700, color:'var(--text-primary)' }}>
            {dept?.id ? 'Modifier le département' : 'Nouveau département'}
          </h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-secondary)', fontSize:'1.3rem' }}>×</button>
        </div>
        <form onSubmit={submit} noValidate style={{ padding:'20px 24px 24px', display:'flex', flexDirection:'column', gap:14 }}>
          <div><label style={lbl}>Nom<Req /></label><input name="name" value={form.name} onChange={set} style={{ ...inp, ...errBorder(errors.name) }} placeholder="ex: Jeunesse" /><FieldError message={errors.name} /></div>
          <div><label style={lbl}>Description</label><input name="description" value={form.description} onChange={set} style={inp} placeholder="Optionnel" /></div>
          <div><label style={lbl}>Budget mensuel (FCFA)</label><input name="monthly_budget" type="number" min="0" value={form.monthly_budget} onChange={set} style={inp} placeholder="Laisser vide = pas de limite" /></div>
          <div>
            <label style={lbl}>Couleur</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:4 }}>
              {PRESET_COLORS.map(c => (
                <button key={c} type="button" onClick={() => setForm(f => ({ ...f, color:c }))}
                  style={{ width:28, height:28, borderRadius:'50%', background:c, border: form.color===c ? `3px solid var(--text-primary)` : '3px solid transparent', cursor:'pointer', outline:'none', boxSizing:'border-box' }} />
              ))}
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color:e.target.value }))}
                style={{ width:28, height:28, borderRadius:'50%', border:'none', cursor:'pointer', padding:0 }} />
            </div>
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:4 }}>
            <button type="button" onClick={onClose} style={btnS}>Annuler</button>
            <button type="submit" disabled={saving} style={{ ...btnP, opacity:saving?0.7:1 }}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────
export default function Accounting() {
  const [tab, setTab]               = useState('dashboard');
  const [dashboard, setDashboard]   = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [noChurch, setNoChurch]     = useState(false);

  const [showTxModal,   setShowTxModal]   = useState(false);
  const [editTx,        setEditTx]        = useState(null);
  const [showCatModal,  setShowCatModal]  = useState(false);
  const [editCat,       setEditCat]       = useState(null);
  const [showDeptModal, setShowDeptModal] = useState(false);
  const [editDept,      setEditDept]      = useState(null);

  // Filtres dashboard
  const [dashMonth,    setDashMonth]    = useState(nowM);
  const [dashYear,     setDashYear]     = useState(nowY);
  const [dashDateFrom, setDashDateFrom] = useState('');
  const [dashDateTo,   setDashDateTo]   = useState('');

  // Filtres transactions
  const [filterMonth,    setFilterMonth]    = useState(nowM);
  const [filterYear,     setFilterYear]     = useState(nowY);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo,   setFilterDateTo]   = useState('');
  const [filterType,     setFilterType]     = useState('');
  const [filterCat,      setFilterCat]      = useState('');
  const [filterDept,     setFilterDept]     = useState('');
  const [filterUser,     setFilterUser]     = useState('');

  const loadDashboard = useCallback(async () => {
    try {
      const params = {};
      if (dashDateFrom || dashDateTo) {
        if (dashDateFrom) params.date_from = dashDateFrom;
        if (dashDateTo)   params.date_to   = dashDateTo;
      } else {
        params.month = dashMonth;
        params.year  = dashYear;
      }
      const res = await api.get('/accounting/dashboard/', { params });
      setDashboard(res.data);
      if (res.data.error) setNoChurch(true);
    } catch { toast.error('Erreur chargement dashboard'); }
  }, [dashMonth, dashYear, dashDateFrom, dashDateTo]);

  const loadTransactions = useCallback(async () => {
    try {
      const params = {};
      if (filterDateFrom || filterDateTo) {
        if (filterDateFrom) params.date_from = filterDateFrom;
        if (filterDateTo)   params.date_to   = filterDateTo;
      } else {
        params.month = filterMonth;
        params.year  = filterYear;
      }
      if (filterType) params.type = filterType;
      if (filterCat)  params.category = filterCat;
      if (filterDept) params.department = filterDept;
      if (filterUser) params.created_by = filterUser;
      const res = await api.get('/accounting/transactions/', { params });
      setTransactions(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch { /* silencieux */ }
  }, [filterMonth, filterYear, filterDateFrom, filterDateTo, filterType, filterCat, filterDept, filterUser]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get('/accounting/categories/');
      setCategories(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch { /* silencieux */ }
  }, []);

  const loadDepartments = useCallback(async () => {
    try {
      const res = await api.get('/accounting/departments/');
      setDepartments(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch { /* silencieux */ }
  }, []);

  useEffect(() => {
    setLoading(true);
    setNoChurch(false);
    Promise.all([loadDashboard(), loadTransactions(), loadCategories(), loadDepartments()])
      .finally(() => setLoading(false));
  }, [loadDashboard, loadTransactions, loadCategories, loadDepartments]);

  const deleteTx = async (id) => {
    if (!window.confirm('Supprimer cette transaction ?')) return;
    try {
      await api.delete(`/accounting/transactions/${id}/`);
      toast.success('Transaction supprimée');
      loadTransactions(); loadDashboard();
    } catch { toast.error('Erreur suppression'); }
  };

  const deleteCat = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await api.delete(`/accounting/categories/${id}/`);
      toast.success('Catégorie supprimée');
      loadCategories(); loadDashboard();
    } catch { toast.error('Erreur suppression'); }
  };

  const deleteDept = async (id) => {
    if (!window.confirm('Supprimer ce département ?')) return;
    try {
      await api.delete(`/accounting/departments/${id}/`);
      toast.success('Département supprimé');
      loadDepartments(); loadDashboard();
    } catch { toast.error('Erreur suppression'); }
  };

  const afterSaveTx   = () => { setShowTxModal(false);   setEditTx(null);   loadTransactions(); loadDashboard(); };
  const afterSaveCat  = () => { setShowCatModal(false);  setEditCat(null);  loadCategories();   loadDashboard(); };
  const afterSaveDept = () => { setShowDeptModal(false); setEditDept(null); loadDepartments();  loadDashboard(); };

  const resetDashFilters = () => { setDashDateFrom(''); setDashDateTo(''); setDashMonth(nowM); setDashYear(nowY); };
  const resetTxFilters   = () => { setFilterDateFrom(''); setFilterDateTo(''); setFilterType(''); setFilterCat(''); setFilterDept(''); setFilterUser(''); setFilterMonth(nowM); setFilterYear(nowY); };

  // ── Export Excel ──────────────────────────────────────────────────────────
  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const period = periodLabel;

    // Feuille 1 — Résumé
    if (dashboard) {
      const summary = [
        ['Rapport Comptabilité — ' + period],
        [],
        ['Indicateur', 'Montant (FCFA)'],
        ['Entrées du mois',   Math.round(dashboard.current_month_income  || 0)],
        ['Sorties du mois',   Math.round(dashboard.current_month_expense || 0)],
        ['Solde',             Math.round((dashboard.current_month_income || 0) - (dashboard.current_month_expense || 0))],
        [],
        ['Prévision entrées fin de mois',  Math.round(dashboard.forecast_income  || 0)],
        ['Prévision sorties fin de mois',  Math.round(dashboard.forecast_expense || 0)],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(summary);
      ws1['!cols'] = [{ wch: 35 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Résumé');
    }

    // Feuille 2 — Transactions
    if (transactions.length > 0) {
      const txRows = [
        ['Date', 'Catégorie', 'Type', 'Description', 'Référence', 'Montant (FCFA)'],
        ...transactions.map(tx => [
          new Date(tx.date).toLocaleDateString('fr-FR'),
          tx.category_name || '',
          tx.category_type === 'income' ? 'Entrée' : 'Sortie',
          tx.description || '',
          tx.reference   || '',
          tx.category_type === 'income' ? Math.round(tx.amount) : -Math.round(tx.amount),
        ]),
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(txRows);
      ws2['!cols'] = [{ wch:14 }, { wch:22 }, { wch:10 }, { wch:30 }, { wch:16 }, { wch:18 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Transactions');
    }

    // Feuille 3 — Budgets par catégorie
    if (dashboard?.categories?.length > 0) {
      const catRows = [
        ['Catégorie', 'Type', 'Dépensé (FCFA)', 'Limite (FCFA)', '% Utilisé', 'Restant (FCFA)'],
        ...dashboard.categories
          .filter(c => c.type === 'expense' && c.monthly_limit)
          .map(c => [
            c.name,
            'Dépense',
            Math.round(c.spent_this_month || 0),
            Math.round(c.monthly_limit || 0),
            `${c.percent_used || 0}%`,
            Math.round(c.remaining || 0),
          ]),
      ];
      const ws3 = XLSX.utils.aoa_to_sheet(catRows);
      ws3['!cols'] = [{ wch:20 }, { wch:10 }, { wch:16 }, { wch:16 }, { wch:12 }, { wch:16 }];
      XLSX.utils.book_append_sheet(wb, ws3, 'Suivi budgets');
    }

    // Feuille 4 — Évolution mensuelle
    if (dashboard?.monthly_summary?.length > 0) {
      const monthRows = [
        ['Mois', 'Entrées (FCFA)', 'Sorties (FCFA)', 'Solde (FCFA)'],
        ...dashboard.monthly_summary.map(m => [
          `${MONTHS_SHORT[m.month - 1]} ${m.year || ''}`.trim(),
          Math.round(m.income  || 0),
          Math.round(m.expense || 0),
          Math.round((m.income || 0) - (m.expense || 0)),
        ]),
      ];
      const ws4 = XLSX.utils.aoa_to_sheet(monthRows);
      ws4['!cols'] = [{ wch:12 }, { wch:18 }, { wch:18 }, { wch:16 }];
      XLSX.utils.book_append_sheet(wb, ws4, 'Évolution mensuelle');
    }

    // Feuille 5 — Départements
    if (dashboard?.departments?.length > 0) {
      const deptRows = [
        ['Département', 'Budget (FCFA)', 'Dépensé (FCFA)', '% Utilisé', 'Restant (FCFA)'],
        ...dashboard.departments.map(d => [
          d.name,
          Math.round(d.monthly_budget || 0),
          Math.round(d.spent_period   || 0),
          d.percent_period != null ? `${d.percent_period}%` : '—',
          Math.round(d.remaining_period ?? 0),
        ]),
      ];
      const ws5 = XLSX.utils.aoa_to_sheet(deptRows);
      ws5['!cols'] = [{ wch:22 }, { wch:18 }, { wch:18 }, { wch:12 }, { wch:16 }];
      XLSX.utils.book_append_sheet(wb, ws5, 'Départements');
    }

    // Feuille 6 — Par utilisateur
    if (dashboard?.by_user?.length > 0) {
      const userRows = [
        ['Utilisateur', 'Total dépensé (FCFA)', 'Nb transactions'],
        ...dashboard.by_user.map(u => [u.name, Math.round(u.total), u.count]),
      ];
      const ws6 = XLSX.utils.aoa_to_sheet(userRows);
      ws6['!cols'] = [{ wch:25 }, { wch:22 }, { wch:18 }];
      XLSX.utils.book_append_sheet(wb, ws6, 'Par utilisateur');
    }

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `comptabilite_${period.replace(/\s/g,'_')}.xlsx`);
    toast.success('Export Excel généré');
  };

  // ── Export PDF ────────────────────────────────────────────────────────────
  const fmtPdf = (n) => String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210;
    const margin = 15;
    const colW = W - margin * 2;
    let y = 20;

    const heading = (text, size = 13) => {
      doc.setFontSize(size); doc.setFont('helvetica', 'bold');
      doc.setTextColor(40, 40, 40);
      doc.text(text, margin, y); y += size * 0.5 + 3;
    };
    const sub = (text, color = [80, 80, 80]) => {
      doc.setFontSize(9); doc.setFont('helvetica', 'normal');
      doc.setTextColor(...color);
      doc.text(text, margin, y); y += 6;
    };
    const row = (cols, widths, bold = false) => {
      doc.setFontSize(9);
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setTextColor(40, 40, 40);
      let x = margin;
      cols.forEach((c, i) => { doc.text(String(c), x, y); x += widths[i]; });
      y += 6;
    };
    const hline = (color = [200, 200, 200]) => {
      doc.setDrawColor(...color);
      doc.line(margin, y, margin + colW, y); y += 4;
    };
    const newPage = () => { doc.addPage(); y = 20; };
    const checkPage = (needed = 12) => { if (y + needed > 280) newPage(); };

    // ── Titre ──
    doc.setFillColor(22, 101, 52);
    doc.rect(0, 0, W, 28, 'F');
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('Rapport de Comptabilité', margin, 13);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`Période : ${periodLabel}`, margin, 21);
    y = 36;

    // ── KPIs ──
    if (dashboard) {
      const income  = Math.round(dashboard.current_month_income  || 0);
      const expense = Math.round(dashboard.current_month_expense || 0);
      const balance = income - expense;
      const boxW = (colW - 8) / 3;

      const kpiBox = (label, value, color, xPos) => {
        doc.setFillColor(...color);
        doc.roundedRect(xPos, y, boxW, 22, 3, 3, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8); doc.setFont('helvetica', 'normal');
        doc.text(label.toUpperCase(), xPos + 4, y + 7);
        doc.setFontSize(14); doc.setFont('helvetica', 'bold');
        const valStr = fmtPdf(value) + ' FCFA';
        doc.text(valStr, xPos + 4, y + 17);
      };
      kpiBox('Entrées',  income,  [16, 185, 129], margin);
      kpiBox('Sorties',  expense, [239, 68, 68],  margin + boxW + 4);
      kpiBox('Solde',    balance, balance >= 0 ? [37, 99, 235] : [239, 68, 68], margin + (boxW + 4) * 2);
      y += 28;
    }

    // ── Alertes budgétaires ──
    if (dashboard?.alerts?.length > 0) {
      checkPage(20);
      heading('Alertes budgétaires', 12); hline();
      dashboard.alerts.forEach(a => {
        checkPage();
        doc.setFillColor(a.type === 'danger' ? 254 : 255, a.type === 'danger' ? 226 : 247, 226);
        doc.roundedRect(margin, y - 4, colW, 10, 2, 2, 'F');
        sub(`${a.category} — ${a.message}`, a.type === 'danger' ? [185, 28, 28] : [180, 83, 9]);
      });
      y += 4;
    }

    // ── Suivi des budgets ──
    if (dashboard?.categories?.filter(c => c.monthly_limit).length > 0) {
      checkPage(20);
      heading('Suivi des budgets — ' + periodLabel, 12); hline();
      const budgetCats = dashboard.categories.filter(c => c.type === 'expense' && c.monthly_limit);
      row(['Catégorie', 'Dépensé', 'Limite', '% Utilisé', 'Restant'], [50, 35, 35, 25, 35], true);
      hline([180, 180, 180]);
      budgetCats.forEach(c => {
        checkPage();
        const pct = c.percent_used || 0;
        const col = pct > 100 ? [239,68,68] : pct >= 80 ? [245,158,11] : [16,185,129];
        doc.setTextColor(...col);
        doc.setFontSize(9); doc.setFont('helvetica', 'normal');
        doc.text(String(c.name), margin, y);
        doc.setTextColor(40, 40, 40);
        doc.text(fmtPdf(c.spent_this_month), margin+50, y);
        doc.text(fmtPdf(c.monthly_limit),   margin+85, y);
        doc.setTextColor(...col);
        doc.text(`${pct}%`, margin+120, y);
        doc.setTextColor(40,40,40);
        doc.text(fmtPdf(c.remaining), margin+145, y);
        y += 6;
      });
      y += 4;
    }

    // ── Transactions ──
    if (transactions.length > 0) {
      // Au moins titre + header + 5 lignes visibles avant saut de page
      checkPage(10 + 4 + 6 + 4 + Math.min(transactions.length, 5) * 6);
      heading(`Transactions (${transactions.length})`, 12); hline();
      row(['Date', 'Catégorie', 'Description', 'Montant FCFA'], [28, 48, 68, 36], true);
      hline([180, 180, 180]);
      transactions.slice(0, 80).forEach((tx, i) => {
        checkPage(10);
        if (i % 2 === 0) { doc.setFillColor(248, 250, 252); doc.rect(margin, y - 4, colW, 6, 'F'); }
        const amount = (tx.category_type === 'income' ? '+' : '-') + fmtPdf(tx.amount);
        doc.setTextColor(80, 80, 80); doc.setFontSize(8.5); doc.setFont('helvetica', 'normal');
        doc.text(new Date(tx.date).toLocaleDateString('fr-FR'), margin, y);
        doc.text(String(tx.category_name || '').slice(0, 20), margin + 28, y);
        doc.text(String(tx.description  || '').slice(0, 35), margin + 76, y);
        doc.setTextColor(tx.category_type === 'income' ? 16 : 220, tx.category_type === 'income' ? 185 : 38, tx.category_type === 'income' ? 129 : 38);
        doc.setFont('helvetica', 'bold');
        doc.text(amount, margin + 144, y);
        y += 6;
      });
      if (transactions.length > 80) {
        checkPage(); sub(`… et ${transactions.length - 80} autres transactions`);
      }
    }

    // ── Évolution mensuelle ──
    if (dashboard?.monthly_summary?.length > 0) {
      // Calcule l'espace total nécessaire : titre(10) + hline(4) + header(6) + hline(4) + lignes(6 chacune)
      const neededEvol = 10 + 4 + 6 + 4 + dashboard.monthly_summary.length * 6 + 6;
      checkPage(neededEvol);
      heading('Évolution mensuelle', 12); hline();
      row(['Mois', 'Entrées (FCFA)', 'Sorties (FCFA)', 'Solde (FCFA)'], [30, 45, 45, 45], true);
      hline([180, 180, 180]);
      dashboard.monthly_summary.forEach(m => {
        const bal = (m.income || 0) - (m.expense || 0);
        row([
          `${MONTHS_SHORT[m.month - 1]} ${m.year || ''}`.trim(),
          fmtPdf(m.income),
          fmtPdf(m.expense),
          fmtPdf(bal),
        ], [30, 45, 45, 45]);
      });
    }

    // ── Footer ──
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(150, 150, 150);
      doc.text(`RCC · Généré le ${new Date().toLocaleDateString('fr-FR')}`, margin, 290);
      doc.text(`Page ${i}/${pageCount}`, W - margin - 20, 290);
    }

    doc.save(`comptabilite_${periodLabel.replace(/\s/g,'_')}.pdf`);
    toast.success('Export PDF généré');
  };

  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setShowExportMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const d = dashboard;
  const periodLabel = dashDateFrom && dashDateTo
    ? `${new Date(dashDateFrom).toLocaleDateString('fr-FR')} – ${new Date(dashDateTo).toLocaleDateString('fr-FR')}`
    : `${MONTHS[dashMonth-1]} ${dashYear}`;

  // Totaux transactions
  const txIncome  = transactions.filter(t=>t.category_type==='income').reduce((s,t)=>s+parseFloat(t.amount),0);
  const txExpense = transactions.filter(t=>t.category_type==='expense').reduce((s,t)=>s+parseFloat(t.amount),0);
  const txBalance = txIncome - txExpense;

  return (
    <div style={{ width:'100%', boxSizing:'border-box' }}>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'20px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h1 style={{ margin:0, fontSize:'1.45rem', fontWeight:800, color:'var(--text-primary)' }}>Comptabilité</h1>
          <p style={{ margin:'3px 0 0', fontSize:'0.84rem', color:'var(--text-secondary)' }}>Suivi des finances de votre église</p>
        </div>
        {!noChurch && (
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
            {/* Bouton Export avec dropdown */}
            <div style={{ position:'relative' }} ref={exportRef}>
              <button style={btnS} onClick={() => setShowExportMenu(v => !v)}>
                <DownloadIcon /> Exporter
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft:2 }}><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {showExportMenu && (
                <div style={{ position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:100, background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:10, boxShadow:'0 8px 24px rgba(0,0,0,0.12)', padding:'6px', minWidth:180 }}>
                  <button onClick={() => { exportExcel(); setShowExportMenu(false); }}
                    style={{ display:'flex', alignItems:'center', gap:'10px', width:'100%', padding:'9px 12px', border:'none', borderRadius:'7px', cursor:'pointer', background:'transparent', textAlign:'left', fontSize:'0.84rem', fontWeight:500, color:'var(--text-primary)', fontFamily:'inherit' }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--page-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    <span style={{ color:'#16a34a', display:'flex' }}><ExcelIcon /></span>
                    <div>
                      <div style={{ fontWeight:600 }}>Export Excel</div>
                      <div style={{ fontSize:'0.72rem', color:'var(--text-secondary)' }}>Résumé, transactions, budgets</div>
                    </div>
                  </button>
                  <button onClick={() => { exportPDF(); setShowExportMenu(false); }}
                    style={{ display:'flex', alignItems:'center', gap:'10px', width:'100%', padding:'9px 12px', border:'none', borderRadius:'7px', cursor:'pointer', background:'transparent', textAlign:'left', fontSize:'0.84rem', fontWeight:500, color:'var(--text-primary)', fontFamily:'inherit' }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--page-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}
                  >
                    <span style={{ color:'#dc2626', display:'flex' }}><PdfIcon /></span>
                    <div>
                      <div style={{ fontWeight:600 }}>Export PDF</div>
                      <div style={{ fontSize:'0.72rem', color:'var(--text-secondary)' }}>Rapport complet avec statistiques</div>
                    </div>
                  </button>
                </div>
              )}
            </div>
            <button style={btnS} onClick={() => { setEditDept(null); setShowDeptModal(true); }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              Département
            </button>
            <button style={btnS} onClick={() => { setEditCat(null); setShowCatModal(true); }}><TagIcon /> Catégorie</button>
            <button style={btnP} onClick={() => { setEditTx(null); setShowTxModal(true); }}><PlusIcon /> Transaction</button>
          </div>
        )}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display:'flex', gap:'2px', background:'#d4c9b8', border:'1px solid #bfb3a0', borderRadius:'10px', padding:'4px', width:'fit-content', marginBottom:'22px' }}>
        {[
          { id:'dashboard',    label:"Vue d'ensemble", Icon: ChartIcon },
          { id:'transactions', label:'Transactions',   Icon: ListIcon  },
          { id:'categories',   label:'Catégories',     Icon: TagIcon   },
          { id:'departments',  label:'Départements',   Icon: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
        ].map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTab(id)} style={{
            display:'flex', alignItems:'center', gap:'6px',
            padding:'7px 15px', borderRadius:'7px', border:'none', cursor:'pointer',
            fontSize:'0.84rem', fontWeight: tab===id ? 600 : 400,
            background: tab===id ? 'var(--primary)' : 'transparent',
            color: tab===id ? '#fff' : '#3D3028',
            transition:'all 0.15s', fontFamily:'inherit',
          }}>
            <Icon /> {label}
          </button>
        ))}
      </div>

      {/* ── Message pas d'église ── */}
      {noChurch && (
        <div style={{ ...card, display:'flex', alignItems:'center', gap:'14px', background:'rgba(99,102,241,0.06)', border:'1px solid rgba(99,102,241,0.2)' }}>
          <div style={{ color:'#6366f1', flexShrink:0 }}><InfoIcon /></div>
          <div>
            <div style={{ fontWeight:600, color:'var(--text-primary)', fontSize:'0.9rem' }}>Aucune église sélectionnée</div>
            <div style={{ color:'var(--text-secondary)', fontSize:'0.82rem', marginTop:'2px' }}>
              Sélectionnez une église via le menu en haut pour accéder à sa comptabilité.
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign:'center', padding:'80px', color:'var(--text-secondary)', fontSize:'0.9rem' }}>Chargement...</div>
      ) : !noChurch && (
        <>
          {/* ════════════════════════════ DASHBOARD ════════════════════════════ */}
          {tab === 'dashboard' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'18px' }}>

              {/* Filtres dashboard */}
              <PeriodFilter
                month={dashMonth} year={dashYear} dateFrom={dashDateFrom} dateTo={dashDateTo}
                type="" cat="" categories={[]}
                onMonth={setDashMonth} onYear={setDashYear}
                onDateFrom={setDashDateFrom} onDateTo={setDashDateTo}
                onType={()=>{}} onCat={()=>{}} onReset={resetDashFilters}
                showType={false} showCat={false}
              />

              {d && (
                <>
                  {/* Période affichée */}
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <CalIcon />
                    <span style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--text-secondary)' }}>Période : </span>
                    <span style={{ fontSize:'0.82rem', fontWeight:700, color:'var(--text-primary)' }}>{periodLabel}</span>
                  </div>

                  {/* KPI row — responsive 3 cols → 1 col mobile */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:'14px' }}>
                    <KpiCard
                      label="Entrées"
                      value={fmt(d.current_month_income)}
                      color="#10b981"
                      Icon={IncomeIcon}
                      sub={<>Prévision fin mois : <strong style={{ color:'var(--text-primary)' }}>{fmt(d.forecast?.projected_income)} FCFA</strong></>}
                    />
                    <KpiCard
                      label="Sorties"
                      value={fmt(d.current_month_expense)}
                      color="#ef4444"
                      Icon={ExpenseIcon}
                      sub={<>Prévision fin mois : <strong style={{ color:'var(--text-primary)' }}>{fmt(d.forecast?.projected_expense)} FCFA</strong></>}
                    />
                    <KpiCard
                      label="Solde"
                      value={(d.balance >= 0 ? '+' : '') + fmt(d.balance)}
                      color={d.balance >= 0 ? 'var(--primary)' : '#ef4444'}
                      Icon={BalanceIcon}
                      sub={<>Jour <strong style={{ color:'var(--text-primary)' }}>{d.forecast?.days_elapsed}</strong> / {d.forecast?.days_in_month}</>}
                    />
                  </div>

                  {/* Alertes + Graphique — 2 cols responsive */}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:'14px' }}>
                    {/* Alertes */}
                    <div style={card}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'14px' }}>
                        <AlertIcon />
                        <span style={{ fontWeight:700, fontSize:'0.87rem', color:'var(--text-primary)' }}>Alertes budgétaires</span>
                        {d.alerts?.length > 0 && (
                          <span style={{ marginLeft:'auto', background:'#ef4444', color:'#fff', fontSize:'0.67rem', fontWeight:700, padding:'2px 8px', borderRadius:'99px' }}>
                            {d.alerts.length}
                          </span>
                        )}
                      </div>
                      {d.alerts?.length > 0 ? d.alerts.map((a, i) => (
                        <div key={i} style={{
                          display:'flex', alignItems:'flex-start', gap:'10px',
                          padding:'10px 12px', borderRadius:'10px', marginBottom:'8px',
                          background: a.type==='danger' ? 'rgba(239,68,68,0.07)' : 'rgba(245,158,11,0.07)',
                          border:`1px solid ${a.type==='danger' ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)'}`,
                        }}>
                          <span style={{ color: a.type==='danger' ? '#ef4444' : '#f59e0b', flexShrink:0, marginTop:'1px' }}><AlertIcon /></span>
                          <div>
                            <div style={{ fontWeight:700, fontSize:'0.82rem', color: a.type==='danger' ? '#ef4444' : '#f59e0b' }}>{a.category}</div>
                            <div style={{ fontSize:'0.77rem', color:'var(--text-secondary)', marginTop:'2px' }}>{a.message}</div>
                          </div>
                        </div>
                      )) : (
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', color:'#10b981', fontSize:'0.84rem', padding:'6px 0' }}>
                          <CheckIcon /> Tous les budgets sont respectés
                        </div>
                      )}
                    </div>

                    {/* Graphique */}
                    <div style={card}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'4px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                          <ChartIcon />
                          <span style={{ fontWeight:700, fontSize:'0.87rem', color:'var(--text-primary)' }}>Évolution 6 mois</span>
                        </div>
                        <div style={{ display:'flex', gap:'12px', fontSize:'0.71rem', color:'var(--text-secondary)' }}>
                          <span style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                            <span style={{ width:9, height:9, background:'#10b981', borderRadius:2, display:'inline-block' }} />Entrées
                          </span>
                          <span style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                            <span style={{ width:9, height:9, background:'#ef4444', borderRadius:2, display:'inline-block' }} />Sorties
                          </span>
                        </div>
                      </div>
                      <BarChart data={d.monthly_summary} />
                    </div>
                  </div>

                  {/* Limites par catégorie */}
                  <div style={card}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'16px' }}>
                      <TrendUpIcon />
                      <span style={{ fontWeight:700, fontSize:'0.87rem', color:'var(--text-primary)' }}>Suivi des budgets — {periodLabel}</span>
                    </div>
                    {d.categories?.filter(c => c.type==='expense' && c.monthly_limit).length > 0 ? (
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px,1fr))', gap:'12px' }}>
                        {d.categories.filter(c => c.type==='expense' && c.monthly_limit).map(c => {
                          const pct = c.percent_used || 0;
                          const col = pColor(pct);
                          return (
                            <div key={c.id} style={{ background:'var(--page-bg)', border:`1px solid ${col}30`, borderLeft:`3px solid ${col}`, borderRadius:'10px', padding:'14px 16px' }}>
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                  <span style={{ width:9, height:9, background:c.color, borderRadius:'50%', flexShrink:0, display:'inline-block' }} />
                                  <span style={{ fontWeight:600, fontSize:'0.86rem', color:'var(--text-primary)' }}>{c.name}</span>
                                </div>
                                <span style={{ padding:'2px 8px', borderRadius:'99px', fontSize:'0.69rem', fontWeight:700, color:col, background:`${col}18` }}>
                                  {pct}%
                                </span>
                              </div>
                              <div style={{ height:6, background:'var(--border)', borderRadius:'99px', overflow:'hidden', marginBottom:'8px' }}>
                                <div style={{ height:6, width:`${Math.min(pct,100)}%`, background:col, borderRadius:'99px', transition:'width 0.5s' }} />
                              </div>
                              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.73rem', color:'var(--text-secondary)' }}>
                                <span>Dépensé : <strong style={{ color:'var(--text-primary)' }}>{fmt(c.spent_this_month)}</strong></span>
                                <span>Limite : <strong style={{ color:'var(--text-primary)' }}>{fmt(c.monthly_limit)}</strong></span>
                              </div>
                              <div style={{ marginTop:'5px', fontSize:'0.72rem', fontWeight:600, color: col }}>
                                {c.remaining > 0 ? `Restant : ${fmt(c.remaining)} FCFA` : pct > 100 ? 'Budget dépassé' : 'Budget atteint'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ color:'var(--text-secondary)', fontSize:'0.875rem', padding:'10px 0' }}>
                        Aucune limite définie. Allez dans <strong>Catégories</strong> pour configurer des limites mensuelles.
                      </div>
                    )}
                  </div>

                  {/* Départements — suivi budget */}
                  {d.departments?.length > 0 && (
                    <div style={card}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                          <span style={{ fontWeight:700, fontSize:'0.87rem', color:'var(--text-primary)' }}>Suivi par département — {periodLabel}</span>
                        </div>
                        <button style={{ ...btnS, padding:'5px 12px', fontSize:'0.78rem' }} onClick={() => setTab('departments')}>Gérer</button>
                      </div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px,1fr))', gap:'12px' }}>
                        {d.departments.map(dept => {
                          const pct = dept.percent_period ?? 0;
                          const col = pct > 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981';
                          return (
                            <div key={dept.id} style={{ background:'var(--page-bg)', border:`1px solid ${dept.color}30`, borderLeft:`3px solid ${dept.color}`, borderRadius:'10px', padding:'14px 16px' }}>
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                                <span style={{ fontWeight:600, fontSize:'0.86rem', color:'var(--text-primary)' }}>{dept.name}</span>
                                {dept.percent_period != null && (
                                  <span style={{ padding:'2px 8px', borderRadius:'99px', fontSize:'0.69rem', fontWeight:700, color:col, background:`${col}18` }}>{pct}%</span>
                                )}
                              </div>
                              {dept.monthly_budget && (
                                <div style={{ height:5, background:'var(--border)', borderRadius:'99px', overflow:'hidden', marginBottom:'7px' }}>
                                  <div style={{ height:5, width:`${Math.min(pct,100)}%`, background:col, borderRadius:'99px', transition:'width 0.5s' }} />
                                </div>
                              )}
                              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.73rem', color:'var(--text-secondary)' }}>
                                <span>Dépensé : <strong style={{ color:'var(--text-primary)' }}>{fmt(dept.spent_period)}</strong></span>
                                {dept.monthly_budget && <span>Budget : <strong>{fmt(dept.monthly_budget)}</strong></span>}
                              </div>
                              {dept.remaining_period != null && (
                                <div style={{ marginTop:'5px', fontSize:'0.72rem', fontWeight:600, color:col }}>
                                  {dept.remaining_period >= 0 ? `Restant : ${fmt(dept.remaining_period)} FCFA` : pct > 100 ? 'Budget dépassé' : 'Budget atteint'}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Par utilisateur */}
                  {d.by_user?.length > 0 && (
                    <div style={card}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'14px' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        <span style={{ fontWeight:700, fontSize:'0.87rem', color:'var(--text-primary)' }}>Dépenses par intervenant — {periodLabel}</span>
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                        {d.by_user.map((u, i) => {
                          const maxTotal = Math.max(...d.by_user.map(x => x.total), 1);
                          const pct = Math.round(u.total / maxTotal * 100);
                          return (
                            <div key={u.user_id || i} style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                              <div style={{ width:32, height:32, borderRadius:'50%', background:'var(--primary)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.75rem', fontWeight:700, flexShrink:0 }}>
                                {u.name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                              </div>
                              <div style={{ flex:1 }}>
                                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                                  <span style={{ fontSize:'0.83rem', fontWeight:600, color:'var(--text-primary)' }}>{u.name}</span>
                                  <span style={{ fontSize:'0.78rem', color:'var(--text-secondary)' }}>{u.count} tx · <strong style={{ color:'#ef4444' }}>{fmt(u.total)} FCFA</strong></span>
                                </div>
                                <div style={{ height:5, background:'var(--border)', borderRadius:'99px', overflow:'hidden' }}>
                                  <div style={{ height:5, width:`${pct}%`, background:'var(--primary)', borderRadius:'99px' }} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Dernières transactions */}
                  {d.recent_transactions?.length > 0 && (
                    <div style={card}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                          <ListIcon />
                          <span style={{ fontWeight:700, fontSize:'0.87rem', color:'var(--text-primary)' }}>Dernières transactions</span>
                        </div>
                        <button style={{ ...btnS, padding:'5px 12px', fontSize:'0.78rem' }} onClick={() => setTab('transactions')}>
                          Voir tout
                        </button>
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                        {d.recent_transactions.slice(0,5).map(tx => (
                          <div key={tx.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 12px', borderRadius:'9px', background:'var(--page-bg)', border:'1px solid var(--border)' }}>
                            <span style={{ width:8, height:8, background:tx.category_color, borderRadius:'50%', flexShrink:0 }} />
                            <span style={{ flex:1, fontSize:'0.83rem', fontWeight:500, color:'var(--text-primary)' }}>{tx.category_name}</span>
                            <span style={{ fontSize:'0.78rem', color:'var(--text-secondary)' }}>{tx.description || ''}</span>
                            <span style={{ fontSize:'0.8rem', color:'var(--text-secondary)', marginLeft:'auto' }}>{new Date(tx.date).toLocaleDateString('fr-FR')}</span>
                            <span style={{ fontWeight:700, fontSize:'0.85rem', color: tx.category_type==='income' ? '#10b981' : '#ef4444', minWidth:90, textAlign:'right' }}>
                              {tx.category_type==='income' ? '+' : '−'}{fmt(tx.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ════════════════════════════ TRANSACTIONS ════════════════════════════ */}
          {tab === 'transactions' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

              {/* Filtres */}
              <PeriodFilter
                month={filterMonth} year={filterYear} dateFrom={filterDateFrom} dateTo={filterDateTo}
                type={filterType} cat={filterCat} categories={categories}
                onMonth={setFilterMonth} onYear={setFilterYear}
                onDateFrom={setFilterDateFrom} onDateTo={setFilterDateTo}
                onType={setFilterType} onCat={setFilterCat} onReset={resetTxFilters}
              />
              {/* Filtres département / intervenant */}
              {(departments.length > 0 || filterDept || filterUser) && (
                <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
                  <div>
                    <label style={lbl}>Département</label>
                    <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={{ ...inp, width:'160px' }}>
                      <option value="">Tous</option>
                      {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Saisi par</label>
                    <select value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ ...inp, width:'160px' }}>
                      <option value="">Tous</option>
                      {(dashboard?.by_user || []).map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* Résumé + bouton */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px' }}>
                <div style={{ display:'flex', gap:'16px', flexWrap:'wrap' }}>
                  <span style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                    Entrées : <strong style={{ color:'#10b981' }}>+{fmt(txIncome)} FCFA</strong>
                  </span>
                  <span style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                    Sorties : <strong style={{ color:'#ef4444' }}>−{fmt(txExpense)} FCFA</strong>
                  </span>
                  <span style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                    Solde : <strong style={{ color: txBalance >= 0 ? 'var(--primary)' : '#ef4444' }}>{fmt(txBalance)} FCFA</strong>
                  </span>
                  <span style={{ fontSize:'0.82rem', color:'var(--text-secondary)' }}>
                    <strong style={{ color:'var(--text-primary)' }}>{transactions.length}</strong> transaction{transactions.length > 1 ? 's' : ''}
                  </span>
                </div>
                <button style={btnP} onClick={() => { setEditTx(null); setShowTxModal(true); }}><PlusIcon /> Ajouter</button>
              </div>

              {/* Table */}
              <div style={card}>
                {transactions.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'50px 20px', color:'var(--text-secondary)' }}>
                    <div style={{ marginBottom:'8px', opacity:0.4 }}><ListIcon /></div>
                    Aucune transaction pour cette période.
                  </div>
                ) : (
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.875rem' }}>
                      <thead>
                        <tr>
                          {['Date','Catégorie','Département','Description','Saisi par','Montant',''].map((h,i) => (
                            <th key={i} style={{ padding:'10px 14px', textAlign: i===5 ? 'right' : 'left', fontSize:'0.7rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'var(--text-secondary)', borderBottom:'2px solid var(--border)', whiteSpace:'nowrap' }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx, idx) => (
                          <tr key={tx.id} style={{ borderBottom:'1px solid var(--border)', background: idx%2===0 ? 'transparent' : 'var(--page-bg)' }}>
                            <td style={{ padding:'11px 14px', color:'var(--text-secondary)', fontSize:'0.8rem', whiteSpace:'nowrap' }}>
                              <div>{new Date(tx.date).toLocaleDateString('fr-FR')}</div>
                              {tx.created_at && (
                                <div style={{ fontSize:'0.72rem', color:'var(--text-secondary)', opacity:0.7, marginTop:'1px' }}>
                                  {new Date(tx.created_at).toLocaleTimeString('fr-FR', { hour:'2-digit', minute:'2-digit' })}
                                </div>
                              )}
                            </td>
                            <td style={{ padding:'11px 14px' }}>
                              <span style={{ display:'flex', alignItems:'center', gap:'7px' }}>
                                <span style={{ width:8, height:8, background:tx.category_color, borderRadius:'50%', flexShrink:0 }} />
                                <span style={{ fontWeight:500, color:'var(--text-primary)' }}>{tx.category_name}</span>
                              </span>
                            </td>
                            <td style={{ padding:'11px 14px' }}>
                              {tx.department_name ? (
                                <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', padding:'2px 8px', borderRadius:'99px', fontSize:'0.72rem', fontWeight:600, background:`${tx.department_color || '#6366f1'}18`, color:tx.department_color || '#6366f1' }}>
                                  <span style={{ width:6, height:6, borderRadius:'50%', background:tx.department_color || '#6366f1', flexShrink:0 }} />{tx.department_name}
                                </span>
                              ) : <span style={{ color:'var(--text-secondary)', fontSize:'0.78rem' }}>—</span>}
                            </td>
                            <td style={{ padding:'11px 14px', color:'var(--text-secondary)', fontSize:'0.82rem' }}>{tx.description || '—'}</td>
                            <td style={{ padding:'11px 14px', color:'var(--text-secondary)', fontSize:'0.77rem' }}>{tx.created_by_name || '—'}</td>
                            <td style={{ padding:'11px 14px', textAlign:'right', fontWeight:700, whiteSpace:'nowrap', color: tx.category_type==='income' ? '#10b981' : '#ef4444' }}>
                              {tx.category_type==='income' ? '+' : '−'}{fmt(tx.amount)} FCFA
                            </td>
                            <td style={{ padding:'11px 14px' }}>
                              <div style={{ display:'flex', gap:'4px', justifyContent:'flex-end' }}>
                                <button onClick={() => { setEditTx(tx); setShowTxModal(true); }}
                                  style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-secondary)', padding:'5px', borderRadius:'6px' }} title="Modifier">
                                  <EditIcon />
                                </button>
                                <button onClick={() => deleteTx(tx.id)}
                                  style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:'5px', borderRadius:'6px' }} title="Supprimer">
                                  <TrashIcon />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ════════════════════════════ DÉPARTEMENTS ════════════════════════════ */}
          {tab === 'departments' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  <span style={{ fontWeight:700, fontSize:'0.88rem', color:'var(--text-primary)' }}>Départements</span>
                  <span style={{ background:'var(--page-bg)', border:'1px solid var(--border)', color:'var(--text-secondary)', fontSize:'0.72rem', padding:'2px 8px', borderRadius:'99px', fontWeight:600 }}>
                    {departments.length}
                  </span>
                </div>
                <button style={btnP} onClick={() => { setEditDept(null); setShowDeptModal(true); }}><PlusIcon /> Nouveau département</button>
              </div>

              {departments.length === 0 ? (
                <div style={{ ...card, textAlign:'center', padding:'50px 20px', color:'var(--text-secondary)' }}>
                  Aucun département. Créez-en un pour organiser vos dépenses.
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:'14px' }}>
                  {departments.map(dept => {
                    const pct = dept.percent_used ?? 0;
                    const col = pct > 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981';
                    return (
                      <div key={dept.id} style={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:'12px', padding:'16px', borderLeft:`3px solid ${dept.color}` }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
                          <div>
                            <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)' }}>{dept.name}</div>
                            {dept.description && <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)', marginTop:'2px' }}>{dept.description}</div>}
                          </div>
                          <div style={{ display:'flex', gap:'2px' }}>
                            <button onClick={() => { setEditDept(dept); setShowDeptModal(true); }} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-secondary)', padding:'4px', borderRadius:'5px' }}><EditIcon /></button>
                            <button onClick={() => deleteDept(dept.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:'4px', borderRadius:'5px' }}><TrashIcon /></button>
                          </div>
                        </div>
                        {dept.monthly_budget && (
                          <>
                            <div style={{ height:5, background:'var(--border)', borderRadius:'99px', overflow:'hidden', marginBottom:'7px' }}>
                              <div style={{ height:5, width:`${Math.min(pct,100)}%`, background:col, borderRadius:'99px' }} />
                            </div>
                            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.73rem', color:'var(--text-secondary)' }}>
                              <span>Dépensé : <strong style={{ color:'var(--text-primary)' }}>{fmt(dept.spent_this_month)}</strong></span>
                              <span style={{ fontWeight:700, color:col }}>{pct}% / {fmt(dept.monthly_budget)}</span>
                            </div>
                          </>
                        )}
                        <div style={{ marginTop:'8px', fontSize:'0.73rem', color:'var(--text-secondary)' }}>
                          {dept.tx_count} transaction{dept.tx_count !== 1 ? 's' : ''} ce mois
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ════════════════════════════ CATÉGORIES ════════════════════════════ */}
          {tab === 'categories' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'10px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <TagIcon />
                  <span style={{ fontWeight:700, fontSize:'0.88rem', color:'var(--text-primary)' }}>Catégories</span>
                  <span style={{ background:'var(--page-bg)', border:'1px solid var(--border)', color:'var(--text-secondary)', fontSize:'0.72rem', padding:'2px 8px', borderRadius:'99px', fontWeight:600 }}>
                    {categories.length}
                  </span>
                </div>
                <button style={btnP} onClick={() => { setEditCat(null); setShowCatModal(true); }}><PlusIcon /> Nouvelle catégorie</button>
              </div>

              {categories.length === 0 ? (
                <div style={{ ...card, textAlign:'center', padding:'50px 20px', color:'var(--text-secondary)' }}>
                  Aucune catégorie. Créez-en une pour commencer.
                </div>
              ) : (
                <>
                  {/* Entrées */}
                  {categories.filter(c=>c.type==='income').length > 0 && (
                    <div>
                      <div style={{ fontSize:'0.75rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#10b981', marginBottom:'10px' }}>Entrées</div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px,1fr))', gap:'12px' }}>
                        {categories.filter(c=>c.type==='income').map(c => <CatCard key={c.id} c={c} onEdit={()=>{setEditCat(c);setShowCatModal(true);}} onDelete={()=>deleteCat(c.id)} />)}
                      </div>
                    </div>
                  )}
                  {/* Sorties */}
                  {categories.filter(c=>c.type==='expense').length > 0 && (
                    <div>
                      <div style={{ fontSize:'0.75rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#ef4444', marginBottom:'10px' }}>Sorties</div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(260px,1fr))', gap:'12px' }}>
                        {categories.filter(c=>c.type==='expense').map(c => <CatCard key={c.id} c={c} onEdit={()=>{setEditCat(c);setShowCatModal(true);}} onDelete={()=>deleteCat(c.id)} />)}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showTxModal && (
        <TransactionModal transaction={editTx} categories={categories} departments={departments}
          onClose={() => { setShowTxModal(false); setEditTx(null); }} onSave={afterSaveTx} />
      )}
      {showCatModal && (
        <CategoryModal category={editCat}
          onClose={() => { setShowCatModal(false); setEditCat(null); }} onSave={afterSaveCat} />
      )}
      {showDeptModal && (
        <DepartmentModal dept={editDept}
          onClose={() => { setShowDeptModal(false); setEditDept(null); }} onSave={afterSaveDept} />
      )}
    </div>
  );
}

// ─── Carte catégorie ──────────────────────────────────────────────────────────
function CatCard({ c, onEdit, onDelete }) {
  const pct = c.percent_used || 0;
  const col = pColor(pct);
  return (
    <div style={{ background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:'12px', padding:'16px', borderLeft:`3px solid ${c.color}` }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
        <div>
          <div style={{ fontWeight:700, fontSize:'0.9rem', color:'var(--text-primary)' }}>{c.name}</div>
          <span style={{ display:'inline-block', marginTop:'4px', padding:'2px 8px', borderRadius:'99px', fontSize:'0.67rem', fontWeight:700,
            color: c.type==='income' ? '#10b981' : '#ef4444',
            background: c.type==='income' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
          }}>
            {c.type==='income' ? 'Entrée' : 'Sortie'}
          </span>
        </div>
        <div style={{ display:'flex', gap:'2px' }}>
          <button onClick={onEdit} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-secondary)', padding:'4px', borderRadius:'5px' }} title="Modifier">
            <EditIcon />
          </button>
          <button onClick={onDelete} style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', padding:'4px', borderRadius:'5px' }} title="Supprimer">
            <TrashIcon />
          </button>
        </div>
      </div>

      {c.type==='expense' && c.monthly_limit && (
        <>
          <div style={{ height:5, background:'var(--border)', borderRadius:'99px', overflow:'hidden', marginBottom:'7px' }}>
            <div style={{ height:5, width:`${Math.min(pct,100)}%`, background:col, borderRadius:'99px' }} />
          </div>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.73rem', color:'var(--text-secondary)' }}>
            <span>{fmt(c.spent_this_month)} FCFA</span>
            <span style={{ fontWeight:700, color:col }}>{pct}% / {fmt(c.monthly_limit)}</span>
          </div>
        </>
      )}
      {c.type==='expense' && !c.monthly_limit && (
        <div style={{ fontSize:'0.74rem', color:'var(--text-secondary)' }}>
          Dépensé ce mois : <strong style={{ color:'var(--text-primary)' }}>{fmt(c.spent_this_month)} FCFA</strong>
        </div>
      )}
      {c.type==='income' && (
        <div style={{ fontSize:'0.74rem', color:'var(--text-secondary)' }}>
          Collecté ce mois : <strong style={{ color:'#10b981' }}>{fmt(c.spent_this_month)} FCFA</strong>
        </div>
      )}
    </div>
  );
}
